"use client";

import { Alert } from "@heroui/alert";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import type { LanguageCode } from "@/config/i18n";
import { ACCOUNT_DELETED_PARAM } from "@/lib/account/constants";
import { createTranslator } from "@/lib/i18n/translations";

interface AccountDeletedBannerProps {
  lang: LanguageCode;
}

/**
 * Confirmation shown after account deletion (`?accountDeleted=1`). Works
 * without a toast provider and survives a reload until dismissed.
 */
export function AccountDeletedBanner({ lang }: AccountDeletedBannerProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  if (searchParams.get(ACCOUNT_DELETED_PARAM) !== "1") return null;

  const t = createTranslator(lang);
  const dismiss = () => {
    const params = new URLSearchParams(searchParams.toString());

    params.delete(ACCOUNT_DELETED_PARAM);

    const query = params.toString();

    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    });
  };

  return (
    <div
      className="mx-auto mt-4 max-w-3xl"
      data-testid="account-deleted-banner"
    >
      <Alert
        closeButtonProps={{ "aria-label": t("account.deleted.dismiss") }}
        color="success"
        description={t("account.deleted.description")}
        title={t("account.deleted.title")}
        variant="faded"
        onClose={dismiss}
      />
    </div>
  );
}
